import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { NAV } from "@/constants/testIds";
import { NAV_ITEMS } from "@/constants/navItems";
import { ROLE_ACCENT, usePermissions } from "@/lib/permissions";
import { LogOut, Moon, Sun, PanelLeftClose, PanelLeftOpen } from "lucide-react";

export function Sidebar() {
  const location = useLocation();
  const { t } = useI18n();
  const { user } = useAuth();
  const { permissions, roles: effectiveRoles, impersonatingAs } = usePermissions();
  const perms = new Set(permissions);
  const rolesSet = new Set(effectiveRoles);
  // A user without any explicit permissions (legacy) sees the plain-user set.
  const effectivePerms = perms.size > 0 ? perms
    : new Set(["self.view_dashboard", "self.edit_profile", "self.view_diagnostic",
               "self.view_goals", "self.view_progress", "self.view_recommendations",
               "self.view_appointments", "self.view_documents", "self.view_messages",
               "self.view_resources", "self.view_subscription"]);
  const items = NAV_ITEMS.filter((item) => {
    if (!item.perm) return true;
    if (effectivePerms.has(item.perm)) return true;
    // Legacy fallback: founder role (real) always sees everything — but NOT
    // when impersonating a persona (the whole point of the persona switch).
    if (!impersonatingAs && (rolesSet.has("founder") || rolesSet.has("super_admin"))) return true;
    return false;
  });

  const primary  = items.filter((i) => i.group === "primary");
  const personal = items.filter((i) => i.group === "personal");
  const admin    = items.filter((i) => i.group === "admin");
  const [personalOpen, setPersonalOpen] = useState(false);
  const [adminOpen,    setAdminOpen]    = useState(false);
  const [darkMode,     setDarkMode]     = useState(() => {
    try { return localStorage.getItem("altiora_sidebar_dark") === "1"; }
    catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("altiora_sidebar_dark", darkMode ? "1" : "0"); }
    catch { /* ignore */ }
  }, [darkMode]);

  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("altiora_sidebar_collapsed") === "1"; }
    catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("altiora_sidebar_collapsed", collapsed ? "1" : "0"); }
    catch { /* ignore */ }
  }, [collapsed]);

  const renderLink = (item) => {
    const active = location.pathname === item.to;
    const Icon = item.icon;
    const label = t(item.label);
    return (
      <Link
        key={item.key}
        to={item.to}
        data-testid={item.testId}
        title={collapsed ? label : undefined}
        aria-label={collapsed ? label : undefined}
        className={`altiora-nav-item flex items-center gap-3 py-2.5 rounded-md text-sm font-medium ${
          collapsed ? "justify-center px-2" : "pl-4 pr-4"
        } ${active ? "altiora-nav-active" : ""}`}
        aria-current={active ? "page" : undefined}
      >
        <Icon className="h-5 w-5 shrink-0" strokeWidth={active ? 2.2 : 1.8} />
        {!collapsed && <span className="truncate">{label}</span>}
      </Link>
    );
  };

  return (
    <aside className={`hidden md:flex flex-col border-r transition-all duration-300 ${
                              darkMode ? "altiora-sidebar-dark" : ""} ${
                              collapsed ? "md:w-16" : "md:w-64"}`}
              style={{ background: darkMode ? undefined : "#F8F7F3",
                          borderColor: darkMode ? undefined : "#E4E1D8" }}
              data-testid="app-sidebar"
              data-collapsed={collapsed ? "1" : "0"}>
      <div className="px-6 py-4 border-b"
              style={{ borderColor: darkMode ? "#C5A88022" : "#E4E1D8" }}>
        {user?.primary_role && (
          <div
            data-testid="sidebar-role-badge"
            className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest px-2 py-1 border rounded-sm"
            style={{ borderColor: (ROLE_ACCENT[impersonatingAs ? effectiveRoles[0] : user.primary_role] || ROLE_ACCENT.member).color,
                     color: (ROLE_ACCENT[impersonatingAs ? effectiveRoles[0] : user.primary_role] || ROLE_ACCENT.member).color }}
          >
            {(ROLE_ACCENT[impersonatingAs ? effectiveRoles[0] : user.primary_role] || ROLE_ACCENT.member).label}
            {impersonatingAs && <span className="ml-1 opacity-60">(vue)</span>}
          </div>
        )}
      </div>
      <nav className="flex-1 py-4 space-y-1 px-3 overflow-y-auto">
        {primary.map(renderLink)}

        {personal.length > 0 && !collapsed && (
          <div className="pt-3 mt-3 border-t border-[#E4E1D8]">
            <button
              type="button"
              onClick={() => setPersonalOpen((v) => !v)}
              data-testid="sidebar-personal-toggle"
              className="w-full flex items-center justify-between px-4 py-2 text-[10px] font-mono uppercase tracking-[0.28em] text-[#7A5B14] hover:text-[#0B2545] transition-colors"
            >
              <span>Mon trajet</span>
              <span className="text-sm">{personalOpen ? "−" : "+"}</span>
            </button>
            {personalOpen && (
              <div className="space-y-1 mt-1" data-testid="sidebar-personal-group">
                {personal.map(renderLink)}
              </div>
            )}
          </div>
        )}
        {personal.length > 0 && collapsed && (
          <div className="pt-3 mt-3 border-t border-[#E4E1D8] space-y-1">
            {personal.map(renderLink)}
          </div>
        )}

        {admin.length > 0 && !collapsed && (
          <div className="pt-3 mt-3 border-t border-[#E4E1D8]">
            <button
              type="button"
              onClick={() => setAdminOpen((v) => !v)}
              data-testid="sidebar-admin-toggle"
              className="w-full flex items-center justify-between px-4 py-2 text-[10px] font-mono uppercase tracking-[0.28em] text-[#5B5B62] hover:text-[#0B2545] transition-colors"
            >
              <span>Administration</span>
              <span className="text-sm">{adminOpen ? "−" : "+"}</span>
            </button>
            {adminOpen && (
              <div className="space-y-1 mt-1" data-testid="sidebar-admin-group">
                {admin.map(renderLink)}
              </div>
            )}
          </div>
        )}
        {admin.length > 0 && collapsed && (
          <div className="pt-3 mt-3 border-t border-[#E4E1D8] space-y-1">
            {admin.map(renderLink)}
          </div>
        )}
      </nav>
      <FooterBlock
        darkMode={darkMode} onToggleDark={() => setDarkMode(v => !v)}
        collapsed={collapsed} onToggleCollapsed={() => setCollapsed(v => !v)} />
    </aside>
  );
}

function FooterBlock({ darkMode, onToggleDark, collapsed, onToggleCollapsed }) {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  return (
    <div className="border-t px-3 py-3"
             style={{ borderColor: darkMode ? "#C5A88022" : "#E4E1D8" }}>
      {!collapsed && (
        <div className="mb-2 px-1 text-xs font-mono truncate"
                style={{ color: darkMode ? "#FBF6EA88" : "#5B5B62" }}>
          {user?.email}
        </div>
      )}
      <div className={`flex items-center gap-2 ${collapsed ? "flex-col" : ""}`}>
        <button
          data-testid={NAV.logout}
          onClick={async () => { await logout(); navigate("/"); }}
          title={t("nav.logout")}
          aria-label={t("nav.logout")}
          className={`${collapsed ? "p-2" : "flex-1 px-3 py-2"} flex items-center gap-2 rounded-md text-sm transition-colors hover:opacity-80`}
          style={{ color: darkMode ? "#FBF6EABB" : "#5B5B62" }}
        >
          <LogOut className="h-4 w-4" strokeWidth={1.5} />
          {!collapsed && t("nav.logout")}
        </button>
        <button
          onClick={onToggleCollapsed}
          data-testid="sidebar-collapse-toggle"
          aria-label={collapsed ? "Étendre la barre latérale" : "Réduire la barre latérale"}
          title={collapsed ? "Étendre" : "Réduire"}
          className="p-1.5 rounded-md transition-colors hover:opacity-80"
          style={{ border: `1px solid ${darkMode ? "#C5A88044" : "#E4E1D8"}`,
                     color: darkMode ? "#FBF6EABB" : "#7A5B14" }}
        >
          {collapsed
            ? <PanelLeftOpen className="h-3.5 w-3.5"  strokeWidth={1.8} />
            : <PanelLeftClose className="h-3.5 w-3.5" strokeWidth={1.8} />}
        </button>
        <button
          onClick={onToggleDark}
          data-testid="sidebar-dark-toggle"
          aria-label={darkMode ? "Passer en mode clair" : "Passer en mode focus"}
          title={darkMode ? "Mode clair" : "Mode focus (sombre)"}
          className="p-1.5 rounded-md transition-colors hover:opacity-80"
          style={{ border: `1px solid ${darkMode ? "#C5A88044" : "#E4E1D8"}`,
                     color: darkMode ? "#F5D687" : "#7A5B14" }}
        >
          {darkMode
            ? <Sun className="h-3.5 w-3.5"  strokeWidth={1.8} />
            : <Moon className="h-3.5 w-3.5" strokeWidth={1.8} />}
        </button>
      </div>
    </div>
  );
}
